import { useTranslation } from "react-i18next";
import "./application_tracking_widget.scss";
import { useEffect, useState } from "react";
import { useEffectOnce } from "react-use";
import { useBoardConfiguration } from "~/hooks/use_board_configuration";
import { Button } from "./button";
import { Body } from "./typography";
import { SnowplowEvent, useSnowplowTracker } from "~/hooks/use_snowplow_tracker";
import serverAssetUrl from "~/utils/asset_url.server";
import { serverOnly$ } from "vite-env-only/macros";

const COPY_VARIANTS = ["a", "b"] as const;
type CopyVariant = (typeof COPY_VARIANTS)[number];
const DEFAULT_COPY_VARIANT: CopyVariant = "a";

const randomCopyVariant = (): CopyVariant =>
  COPY_VARIANTS[Math.floor(Math.random() * COPY_VARIANTS.length)];

const variantEventData = (trackedVariant?: CopyVariant) =>
  trackedVariant ? { variant: `copy_${trackedVariant}` } : {};

const variantUrlParam = (trackedVariant?: CopyVariant) =>
  trackedVariant ? `&variant=copy_${trackedVariant}` : "";

const ApplicationTrackingWidget = () => {
  const { t } = useTranslation("job_post");
  const [jobSeekersUrl, setJobSeekersUrl] = useState("");
  const [copyVariant, setCopyVariant] = useState<CopyVariant>();
  const { preview_mode, button_shape } = useBoardConfiguration();
  const { trackEvent } = useSnowplowTracker();
  const assetUrl = serverOnly$(serverAssetUrl) || window.ENV.ASSET_URL;

  const useVariants = !!copyVariant && window.ENV.TRACK_STATUS_COPY_VARIANTS;

  useEffectOnce(() => {
    setCopyVariant(
      window.ENV.TRACK_STATUS_COPY_VARIANTS ? randomCopyVariant() : DEFAULT_COPY_VARIANT
    );
  });

  useEffect(() => {
    copyVariant &&
      !preview_mode &&
      trackEvent(SnowplowEvent.MyGreenhouseAdShown, {
        type: "track_your_status",
        ...variantEventData(useVariants ? copyVariant : undefined),
      });
  });

  useEffect(() => {
    setJobSeekersUrl(
      `${window.ENV.JOB_SEEKERS_URL}/users/sign_in?source=track_your_status${variantUrlParam(
        useVariants ? copyVariant : undefined
      )}`
    );
  }, [copyVariant, useVariants]);

  const trackClick = () => {
    !preview_mode &&
      trackEvent(SnowplowEvent.MyGreenhouseAdClicked, {
        type: "track_your_status",
        ...variantEventData(useVariants ? copyVariant : undefined),
      });
  };

  return (
    <div className="application_tracking_widget">
      <div className="application_tracking_widget__icon">
        <img src={`${assetUrl}/greenhouse_white_g.svg`} alt="MyGreenhouse Application Tracking" />
      </div>
      <div className="application_tracking_widget__content">
        <div className="application_tracking_widget__content__header">
          <Body medium>{t("application_tracking_widget.header")}</Body>
          <Body>{copyVariant && t(`application_tracking_widget.body_${copyVariant}`)}</Body>
        </div>
        <Button
          secondary
          link
          href={preview_mode ? undefined : jobSeekersUrl}
          target="_blank"
          shape={button_shape}
          onClick={trackClick}
        >
          {copyVariant && t(`application_tracking_widget.create_account_${copyVariant}`)}
        </Button>
      </div>
    </div>
  );
};

export default ApplicationTrackingWidget;
